import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Settings,
  Plus,
  Calendar,
  PiggyBank,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Lock,
  Sparkles,
  Trash2,
  Receipt,
  ShoppingCart,
  Trophy,
  Target,
  Repeat,
  Lightbulb
} from 'lucide-react';

// Icon mapping for insights
const insightIcons = {
  'calendar': Calendar,
  'shopping-cart': ShoppingCart,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'trophy': Trophy,
  'target': Target,
  'alert-triangle': AlertTriangle,
  'repeat': Repeat
};

// Color mapping for insights
const insightColors = {
  'blue': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  'green': 'bg-green-500/10 text-green-600 border-green-500/20',
  'orange': 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  'yellow': 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  'red': 'bg-red-500/10 text-red-600 border-red-500/20',
  'purple': 'bg-purple-500/10 text-purple-600 border-purple-500/20'
};

const BudgetSection = () => {
  const { api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isProPlus, setIsProPlus] = useState(false);
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [spendingOpen, setSpendingOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [budgetSettings, setBudgetSettings] = useState({
    weekly_budget: '',
    monthly_budget: ''
  });
  
  const [spendingEntry, setSpendingEntry] = useState({
    amount: '',
    notes: '',
    source: 'manual'
  });

  // Check subscription and load budget data
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        // Check subscription
        const subResponse = await api.get('/subscription/status');
        const isPP = subResponse.data.is_pro && subResponse.data.plan === 'plus';
        setIsProPlus(isPP);
        
        if (isPP) {
          // Load budget summary and insights in parallel
          const [summaryResponse, settingsResponse, insightsResponse] = await Promise.all([
            api.get('/budget/summary'),
            api.get('/budget'),
            api.get('/budget/insights')
          ]);
          
          setSummary(summaryResponse.data);
          setInsights(insightsResponse.data);
          setBudgetSettings({
            weekly_budget: settingsResponse.data.weekly_budget || '',
            monthly_budget: settingsResponse.data.monthly_budget || ''
          });
        }
      } catch (error) {
        console.error('Failed to load budget data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [api, isAuthenticated]);

  const saveBudgetSettings = async () => {
    setSaving(true);
    try {
      await api.put('/budget', {
        weekly_budget: budgetSettings.weekly_budget ? parseFloat(budgetSettings.weekly_budget) : null,
        monthly_budget: budgetSettings.monthly_budget ? parseFloat(budgetSettings.monthly_budget) : null
      });
      
      toast.success('Budget settings saved!');
      setSettingsOpen(false);
      
      // Reload summary and insights
      const [summaryResponse, insightsResponse] = await Promise.all([
        api.get('/budget/summary'),
        api.get('/budget/insights')
      ]);
      setSummary(summaryResponse.data);
      setInsights(insightsResponse.data);
    } catch (error) {
      toast.error('Failed to save budget settings');
    } finally {
      setSaving(false);
    }
  };

  const logSpending = async () => {
    if (!spendingEntry.amount || parseFloat(spendingEntry.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/budget/spending', {
        amount: parseFloat(spendingEntry.amount),
        notes: spendingEntry.notes || null,
        source: spendingEntry.source
      });
      
      toast.success('Spending logged!');
      setSpendingOpen(false);
      setSpendingEntry({ amount: '', notes: '', source: 'manual' });
      
      // Reload summary and insights
      const [summaryResponse, insightsResponse] = await Promise.all([
        api.get('/budget/summary'),
        api.get('/budget/insights')
      ]);
      setSummary(summaryResponse.data);
      setInsights(insightsResponse.data);
    } catch (error) {
      toast.error('Failed to log spending');
    } finally {
      setSaving(false);
    }
  };

  const deleteSpendingEntry = async (entryId) => {
    try {
      await api.delete(`/budget/spending/${entryId}`);
      toast.success('Entry deleted');
      
      // Reload summary and insights
      const [summaryResponse, insightsResponse] = await Promise.all([
        api.get('/budget/summary'),
        api.get('/budget/insights')
      ]);
      setSummary(summaryResponse.data);
      setInsights(insightsResponse.data);
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-orange-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-primary';
  };

  const getStatusIcon = (percentage) => {
    if (percentage >= 100) return <AlertTriangle className="h-4 w-4 text-red-500" />;
    if (percentage >= 80) return <TrendingUp className="h-4 w-4 text-orange-500" />;
    return <CheckCircle2 className="h-4 w-4 text-primary" />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  // Show upgrade prompt for non Pro Plus users
  if (!isProPlus) {
    return (
      <Card className="border-dashed border-2 border-muted-foreground/20">
        <CardHeader>
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <PiggyBank className="h-5 w-5" />
            Budget Planning
            <Badge variant="secondary" className="ml-2">
              <Lock className="h-3 w-3 mr-1" />
              Pro Plus
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <DollarSign className="h-8 w-8 text-primary/50" />
            </div>
            <h3 className="font-semibold mb-2">Track Your Grocery Spending</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              Set weekly and monthly budgets, log spending after shopping trips, and see your spending trends over time.
            </p>
            <Button onClick={() => navigate('/pricing')} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Upgrade to Pro Plus
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show setup prompt if no budget is set
  if (!summary?.budget_set) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <PiggyBank className="h-5 w-5" />
            Budget Planning
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <DollarSign className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold mb-2">Set Your Grocery Budget</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              Define your weekly and/or monthly grocery budget to start tracking your spending.
            </p>
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="setup-budget-btn">
                  <Settings className="h-4 w-4" />
                  Set Up Budget
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Playfair_Display']">Budget Settings</DialogTitle>
                  <DialogDescription>
                    Set your weekly and/or monthly grocery budget
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Weekly Budget ($)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 100"
                      value={budgetSettings.weekly_budget}
                      onChange={(e) => setBudgetSettings(prev => ({ ...prev, weekly_budget: e.target.value }))}
                      min="0"
                      step="0.01"
                      data-testid="weekly-budget-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Budget ($)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 400"
                      value={budgetSettings.monthly_budget}
                      onChange={(e) => setBudgetSettings(prev => ({ ...prev, monthly_budget: e.target.value }))}
                      min="0"
                      step="0.01"
                      data-testid="monthly-budget-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
                  <Button onClick={saveBudgetSettings} disabled={saving} data-testid="save-budget-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Budget
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <PiggyBank className="h-5 w-5" />
            Budget Planning
          </CardTitle>
          <div className="flex gap-2">
            {/* Log Spending Button */}
            <Dialog open={spendingOpen} onOpenChange={setSpendingOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="log-spending-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Log Spending
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Playfair_Display']">Log Spending</DialogTitle>
                  <DialogDescription>
                    Record your grocery spending
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Amount ($) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 52.47"
                      value={spendingEntry.amount}
                      onChange={(e) => setSpendingEntry(prev => ({ ...prev, amount: e.target.value }))}
                      min="0"
                      step="0.01"
                      data-testid="spending-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Input
                      placeholder="e.g., Weekly groceries at Walmart"
                      value={spendingEntry.notes}
                      onChange={(e) => setSpendingEntry(prev => ({ ...prev, notes: e.target.value }))}
                      data-testid="spending-notes-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSpendingOpen(false)}>Cancel</Button>
                  <Button onClick={logSpending} disabled={saving} data-testid="save-spending-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Receipt className="h-4 w-4 mr-2" />}
                    Log Spending
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Settings Button */}
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="ghost" data-testid="budget-settings-btn">
                  <Settings className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Playfair_Display']">Budget Settings</DialogTitle>
                  <DialogDescription>
                    Update your weekly and monthly budget
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Weekly Budget ($)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 100"
                      value={budgetSettings.weekly_budget}
                      onChange={(e) => setBudgetSettings(prev => ({ ...prev, weekly_budget: e.target.value }))}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Budget ($)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 400"
                      value={budgetSettings.monthly_budget}
                      onChange={(e) => setBudgetSettings(prev => ({ ...prev, monthly_budget: e.target.value }))}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
                  <Button onClick={saveBudgetSettings} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Budget Progress Cards */}
        <Tabs defaultValue="weekly" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="weekly">Weekly</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
          
          {/* Weekly Tab */}
          <TabsContent value="weekly" className="mt-4">
            {summary?.weekly?.budget ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(summary.weekly.percentage)}
                    <span className="font-medium">
                      ${summary.weekly.spent.toFixed(2)} of ${summary.weekly.budget.toFixed(2)}
                    </span>
                  </div>
                  <Badge variant={summary.weekly.percentage >= 100 ? "destructive" : "secondary"}>
                    {summary.weekly.percentage.toFixed(0)}%
                  </Badge>
                </div>
                <Progress 
                  value={Math.min(summary.weekly.percentage, 100)} 
                  className={`h-3 ${getProgressColor(summary.weekly.percentage)}`}
                />
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    {summary.weekly.remaining >= 0 
                      ? `$${summary.weekly.remaining.toFixed(2)} remaining`
                      : `$${Math.abs(summary.weekly.remaining).toFixed(2)} over budget`
                    }
                  </span>
                  <span className="text-xs">
                    {new Date(summary.weekly.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(summary.weekly.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <p>No weekly budget set</p>
                <Button variant="link" size="sm" onClick={() => setSettingsOpen(true)}>
                  Set weekly budget
                </Button>
              </div>
            )}
          </TabsContent>
          
          {/* Monthly Tab */}
          <TabsContent value="monthly" className="mt-4">
            {summary?.monthly?.budget ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(summary.monthly.percentage)}
                    <span className="font-medium">
                      ${summary.monthly.spent.toFixed(2)} of ${summary.monthly.budget.toFixed(2)}
                    </span>
                  </div>
                  <Badge variant={summary.monthly.percentage >= 100 ? "destructive" : "secondary"}>
                    {summary.monthly.percentage.toFixed(0)}%
                  </Badge>
                </div>
                <Progress 
                  value={Math.min(summary.monthly.percentage, 100)} 
                  className={`h-3 ${getProgressColor(summary.monthly.percentage)}`}
                />
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    {summary.monthly.remaining >= 0 
                      ? `$${summary.monthly.remaining.toFixed(2)} remaining`
                      : `$${Math.abs(summary.monthly.remaining).toFixed(2)} over budget`
                    }
                  </span>
                  <span className="text-xs">
                    {new Date(summary.monthly.period_start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <p>No monthly budget set</p>
                <Button variant="link" size="sm" onClick={() => setSettingsOpen(true)}>
                  Set monthly budget
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Spending Insights */}
        {insights?.has_data && insights?.insights?.length > 0 && (
          <div className="pt-4 border-t">
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              Spending Insights
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {insights.insights.map((insight, index) => {
                const IconComponent = insightIcons[insight.icon] || Lightbulb;
                const colorClasses = insightColors[insight.color] || insightColors.blue;
                
                return (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg border ${colorClasses}`}
                    data-testid={`insight-${insight.type}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <IconComponent className="h-4 w-4" />
                      <span className="text-xs font-semibold">{insight.title}</span>
                    </div>
                    <p className="text-xs opacity-80">{insight.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Recent Spending */}
        {summary?.recent_entries?.length > 0 && (
          <div className="pt-4 border-t">
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Recent Spending
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {summary.recent_entries.slice(0, 5).map((entry) => (
                <div 
                  key={entry.id} 
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/30 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <ShoppingCart className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">${entry.amount.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {entry.notes && ` • ${entry.notes}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => deleteSpendingEntry(entry.id)}
                  >
                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BudgetSection;
